/* iter-greyrom-followup-2 — Cross-shelf reader tag preferences.
 *
 * The Grey Romance shelf's per-shelf tag-filter chips persist to
 * localStorage under `bs-shelf-excluded-tags:{slug}`. This hook
 * scans every such key at mount time, unions them into a single
 * "global excluded tags" set, and exposes helpers to filter any
 * book-array or reset the preference site-wide.
 *
 * There is deliberately no server round-trip — the preference is a
 * privacy-sensitive reader-warning setting and belongs on-device.
 */
import { useEffect, useState, useCallback } from 'react';

import { bookstoreApi } from '@/lib/bookstoreApi';

const SHELF_KEY_PREFIX = 'bs-shelf-excluded-tags:';
const GLOBAL_KEY       = 'bs-excluded-tags-global';
const OPT_OUT_KEY      = 'bs-telemetry-opt-out';

function readGlobalSet() {
  try {
    const raw = localStorage.getItem(GLOBAL_KEY);
    if (raw) return new Set(JSON.parse(raw));
  } catch { /* noop */ }
  return new Set();
}

function readAllShelfSets() {
  const merged = new Set();
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(SHELF_KEY_PREFIX)) continue;
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      try {
        JSON.parse(raw).forEach((t) => merged.add(t));
      } catch { /* skip malformed */ }
    }
  } catch { /* SSR / private-mode: fall through */ }
  return merged;
}

export default function useReaderTagPreferences() {
  // Combined view = union(global, every-shelf-preference). Recomputed
  // on mount and whenever another tab writes localStorage.
  const [excluded, setExcluded] = useState(() => new Set([
    ...readGlobalSet(),
    ...readAllShelfSets(),
  ]));

  const recompute = useCallback(() => {
    setExcluded(new Set([...readGlobalSet(), ...readAllShelfSets()]));
  }, []);

  useEffect(() => {
    const onStorage = (e) => {
      if (!e.key) return;
      if (e.key === GLOBAL_KEY || e.key.startsWith(SHELF_KEY_PREFIX)) recompute();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [recompute]);

  const clearAll = useCallback(() => {
    try {
      // iter-romance26 — Fire a "show" telemetry beacon per tag before
      // we wipe local state (respects the opt-out sentinel).
      const optedOut = (() => { try { return localStorage.getItem(OPT_OUT_KEY) === '1'; } catch { return false; } })();
      if (!optedOut) {
        excluded.forEach((tag) => {
          try { bookstoreApi.tagHiddenBeacon?.({ tag, action: 'show' }); } catch { /* noop */ }
        });
      }
      localStorage.removeItem(GLOBAL_KEY);
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (key && key.startsWith(SHELF_KEY_PREFIX)) keysToRemove.push(key);
      }
      keysToRemove.forEach((k) => localStorage.removeItem(k));
    } catch { /* noop */ }
    setExcluded(new Set());
  }, [excluded]);

  /** Filter a list of book-like objects, removing anything carrying
   *  a currently-excluded content_tag. */
  const applyTo = useCallback((books) => {
    if (!excluded.size || !Array.isArray(books)) return books || [];
    return books.filter(
      (b) => !(b.content_tags || []).some((t) => excluded.has(t)),
    );
  }, [excluded]);

  return { excluded, hasPreferences: excluded.size > 0, applyTo, clearAll };
}
